/**
 * ABR filter table component using DataList pattern.
 * Follows the same conventions as ModerationQueueTable.
 */

import { DataList, type TableColumn } from '@core/components';
import type { AbrFilter } from '@moderation/domain/AbrFilter';
import { ButtonType, Delete, Edit, IconButton, IconSize, OverlayMode, Text } from '@soteria/design-system-ve';
import { type MouseEvent, useMemo } from 'react';

import * as styles from './AbrFilterTable.css';

interface AbrFilterTableProps {
  filters: AbrFilter[];
  isLoading: boolean;
  onEdit: (filter: AbrFilter) => void;
  onDelete: (filter: AbrFilter) => void;
}

export function AbrFilterTable({ filters, isLoading, onEdit, onDelete }: AbrFilterTableProps) {
  const columns = useMemo<TableColumn<AbrFilter>[]>(
    () => [
      {
        key: 'name',
        header: 'Name',
        render: (filter: AbrFilter) => <Text>{filter.name}</Text>,
        width: '25%',
      },
      {
        key: 'description',
        header: 'Description',
        render: (filter: AbrFilter) => <Text>{filter.description || '—'}</Text>,
        width: '25%',
      },
      {
        key: 'type',
        header: 'Type',
        render: (filter: AbrFilter) => <Text>{filter.criteria.reportedContent ? 'Reported content' : 'Classified content'}</Text>,
        width: '15%',
      },
      {
        key: 'createdAt',
        header: 'Created',
        render: (filter: AbrFilter) => <Text>{new Date(filter.createdAt).toLocaleDateString()}</Text>,
        width: '15%',
      },
      {
        key: 'actions',
        header: '',
        render: (filter: AbrFilter) => (
          <div className={styles.actionsRow}>
            <IconButton
              aria-label={`Edit ${filter.name}`}
              buttonType={ButtonType.Primary}
              icon={Edit}
              iconSize={IconSize.XSmall}
              overlayMode={OverlayMode.Solid}
              onClick={(e: MouseEvent) => {
                e.stopPropagation();
                onEdit(filter);
              }}
            />
            <IconButton
              aria-label={`Delete ${filter.name}`}
              buttonType={ButtonType.Danger}
              icon={Delete}
              iconSize={IconSize.XSmall}
              overlayMode={OverlayMode.Solid}
              onClick={(e: MouseEvent) => {
                e.stopPropagation();
                onDelete(filter);
              }}
            />
          </div>
        ),
        width: '20%',
      },
    ],
    [onEdit, onDelete]
  );

  return <DataList columns={columns} data={filters} dataTypeName="queue filter" isLoading={isLoading} />;
}
